/** Shared inline SVG icon set. Every icon inherits currentColor so color comes
 *  from the surrounding text token and a rebrand is free (CapMark precedent).
 *  UI controls use these — emoji are reserved for voice/content. */

interface IconProps {
  className?: string;
}

function base(className?: string) {
  return {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    className,
    "aria-hidden": true as const
  };
}

/** Graduation-cap mark — single color, inherits currentColor so it follows the
 *  surrounding text token and a rebrand is free. Matches public/favicon.svg. */
export function CapMark({ className }: IconProps): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 44 44"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <polygon points="22,6 42,16 22,26 2,16" />
      <path d="M8 19.5V30c0 0 5 6 14 6s14-6 14-6V19.5" />
      <line x1="42" y1="16" x2="42" y2="28" />
      <circle cx="42" cy="29" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function CheckIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base(className)}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export function LockIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base(className)}>
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

export function FlameIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base(className)}>
      <path d="M12 3c1 3-3 4.5-3 8a3 3 0 0 0 6 0c0-1-.5-2-1-2.5 2 .5 4 2.5 4 5.5a6 6 0 0 1-12 0c0-5 4-7.5 6-11z" />
    </svg>
  );
}

export function BulbIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base(className)}>
      <path d="M9 18h6" />
      <path d="M10 21h4" />
      <path d="M12 3a6 6 0 0 0-4 10.5c.6.6 1 1.4 1 2.5h6c0-1.1.4-1.9 1-2.5A6 6 0 0 0 12 3z" />
    </svg>
  );
}

export function PlayIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base(className)}>
      <polygon points="7 4 20 12 7 20 7 4" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function ResetIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base(className)}>
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <polyline points="3 4 3 9 8 9" />
    </svg>
  );
}

export function CloseIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base(className)}>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}
